import React, { useEffect, useRef, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Box } from '@chakra-ui/react';
import LandingHeader from '../../components/LandingHeader/LandingHeader';
import LandingFooter from '../../components/LandingFooter/LandingFooter';
import Icon from '../../components/ui/Icon';
import Turnstile from '../../components/Turnstile';
import Seo from '../../components/Seo/Seo';
import { getTeamMember } from './teamMembers';
import { captureLead, LEAD_SEGMENTS } from '../../utils/leadCapture';
import { notifyLead } from '../../utils/leadNotify';
import './Landing.css';
import '../../components/LandingFooter/LandingFooter.css';
import './TeamMember.css';

const MotionBox = motion(Box);

export default function TeamMember() {
  const { memberId } = useParams();
  const member = getTeamMember(memberId);

  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const turnstileRef = useRef(null);

  // Reset the form when switching between team member profiles, since the
  // component stays mounted and only the route param changes.
  useEffect(() => {
    setForm({ name: '', email: '', message: '' });
    setSubmitted(false);
    setSubmitting(false);
    setError('');
    setCaptchaToken('');
    turnstileRef.current?.reset();
  }, [memberId]);

  // A member that doesn't exist falls back to the team page.
  if (!member) {
    return <Navigate to="/about/team" replace />;
  }

  const onField = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    if (error) setError('');
  };
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken || submitting) return;
    setSubmitting(true);
    setError('');

    // Routes through the company (no personal email exposed). The submission is
    // persisted as a contact lead, tagged with the team member it targeted.
    const { error: captureError } = await captureLead({
      email: form.email,
      segment: LEAD_SEGMENTS.CONTACT,
      source: 'team_member',
      name: form.name,
      payload: {
        memberId,
        memberName: member.name,
        message: form.message,
      },
      token: captchaToken,
    });

    // The Turnstile token is single-use; clear it whatever the outcome.
    turnstileRef.current?.reset();
    setCaptchaToken('');
    setSubmitting(false);

    if (captureError) {
      setError('Something went wrong sending your message. Please try again.');
      return;
    }

    // Auto-reply to the visitor and alert the team. Fire-and-forget: the
    // message is already saved, so a mail hiccup must not block the success view.
    notifyLead({ email: form.email, segment: LEAD_SEGMENTS.CONTACT });

    setSubmitted(true);
  };

  const firstName = member.name.split(' ')[0];

  return (
    <div className="landing-page">
      <Seo
        title={`${member.name} | ${member.role} at Qentoros`}
        description={
          member.tagline
            ? `${member.name}, ${member.role} at Qentoros. ${member.tagline}`
            : `${member.name} is ${member.role} at Qentoros, advancing BIO-PLY™ and equine biologics.`
        }
        path={`/about/team/${member.slug}`}
      />
      <LandingHeader />

      <main id="main" tabIndex={-1}>
        {/* Profile hero */}
        <section className={`tm-hero tm-hero--${member.accent}`} aria-label={`${member.name} profile`}>
          <div className="tm-hero-inner">
            <MotionBox
              className="tm-hero-copy"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link to="/about/team" className="tm-back-link">
                <Icon name="arrow_back" aria-hidden="true" />
                Back to the team
              </Link>
              <p className="tm-hero-eyebrow">{member.role}</p>
              <h1 className="tm-hero-name">{member.name}</h1>
              <ul className="tm-hero-credentials" aria-label={`${member.name} credentials`}>
                {member.credentials.map((c) => (
                  <li key={c} className="tm-credential-pill">{c}</li>
                ))}
              </ul>
              <p className="tm-hero-tagline">{member.tagline}</p>
              {member.linkedin && (
                <a
                  className="tm-hero-linkedin"
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${member.name} on LinkedIn`}
                >
                  <Icon name="linkedin" aria-hidden="true" />
                  Connect on LinkedIn
                </a>
              )}
            </MotionBox>

            <MotionBox
              className="tm-hero-portrait"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              aria-hidden="true"
            >
              {member.photo ? (
                <img src={member.photo} alt="" className="tm-hero-photo" />
              ) : (
                <span className="tm-hero-initials">{member.initials}</span>
              )}
            </MotionBox>
          </div>
        </section>

        {/* Bio + focus areas */}
        <section className="landing-section tm-body" aria-labelledby="tm-bio-heading">
          <div className="tm-body-grid">
            <div className="tm-bio">
              <p className="tm-section-eyebrow">Background</p>
              <h2 id="tm-bio-heading" className="tm-section-title">About {firstName}</h2>
              {member.profileBio.map((para, i) => (
                <p key={i} className="tm-bio-para">{para}</p>
              ))}
            </div>

            <div className="tm-focus">
              <p className="tm-section-eyebrow">Areas of focus</p>
              <ul className="tm-focus-list">
                {member.focus.map((item) => (
                  <li key={item.title} className="tm-focus-item">
                    <Icon name="check_circle" className="tm-focus-icon" aria-hidden="true" />
                    <div>
                      <h3 className="tm-focus-title">{item.title}</h3>
                      <p className="tm-focus-body">{item.body}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Tailored contact CTA — routes through the company, no email exposed */}
        <section id="contact" className="tm-contact" aria-labelledby="tm-contact-heading">
          <div className="tm-contact-inner">
            <div className="tm-contact-message">
              <p className="tm-contact-eyebrow">Get in touch</p>
              <h2 id="tm-contact-heading" className="tm-contact-title">
                Reach {firstName} and the Qentoros team
              </h2>
              <p className="tm-contact-sub">
                Questions about {firstName}’s work, {member.role.toLowerCase()} matters, partnership,
                or clinical use of BIO-PLY™? Send a note and the right person will follow up.
              </p>
            </div>

            <div className="tm-contact-card">
              {submitted ? (
                <div className="tm-contact-success">
                  <div className="tm-contact-success-icon">✓</div>
                  <h3 className="tm-contact-success-title">Message sent</h3>
                  <p className="tm-contact-success-body">
                    Thanks, your message is on its way to the Qentoros team. We’ll be in touch shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="tm-form">
                  <p className="tm-form-label">Contact {firstName}</p>
                  <input
                    className="tm-input"
                    name="name"
                    value={form.name}
                    onChange={onField}
                    placeholder="Your name"
                    aria-label="Your name"
                  />
                  <input
                    className="tm-input"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onField}
                    placeholder="Your email"
                    aria-label="Your email"
                  />
                  <textarea
                    className="tm-input tm-textarea"
                    name="message"
                    value={form.message}
                    onChange={onField}
                    rows={4}
                    placeholder={`How can ${firstName} and the team help?`}
                    aria-label="Your message"
                  />
                  <Turnstile
                    ref={turnstileRef}
                    onVerify={setCaptchaToken}
                    onExpire={() => setCaptchaToken('')}
                    onError={() => setCaptchaToken('')}
                  />
                  <button type="submit" className="tm-submit" disabled={!captchaToken || submitting}>{submitting ? 'Sending…' : 'Send message'}</button>
                  {error && (
                    <p role="alert" className="tm-form-note" style={{ color: '#B42318' }}>{error}</p>
                  )}
                  <p className="tm-form-note">Your message routes through Qentoros. We respond within 24 hours.</p>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
}
